import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { type SubscriptionStatus } from "../lib/useSubscription";

const API_URL = import.meta.env.VITE_API_URL as string;

interface Props {
  email: string | null;
  status: SubscriptionStatus;
  trial_ends_at: string | null;
  current_period_end: string | null;
  square_subscription_id: string | null;
  onSignOut: () => void;
  onUpgrade: () => void;
  onBack: () => void;
}

interface BillingRow {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: "PAID" | "UNPAID" | "SCHEDULED" | "FAILED" | "CANCELED";
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD" }).format(amount);
}

const STATUS_STYLE: Record<BillingRow["status"], { label: string; color: string; bg: string }> = {
  PAID:      { label: "Paid",      color: "#0d9488", bg: "rgba(13,148,136,0.12)" },
  SCHEDULED: { label: "Scheduled", color: "#0ea5e9", bg: "rgba(14,165,233,0.12)" },
  UNPAID:    { label: "Unpaid",    color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  FAILED:    { label: "Failed",    color: "#ef4444", bg: "rgba(239,68,68,0.12)"  },
  CANCELED:  { label: "Canceled",  color: "#64748b", bg: "rgba(100,116,139,0.12)"},
};

export default function AccountScreen({ email, status, trial_ends_at, current_period_end, square_subscription_id, onSignOut, onUpgrade, onBack }: Props) {
  const [billing, setBilling] = useState<BillingRow[]>([]);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingError, setBillingError] = useState(false);

  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelDone, setCancelDone] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    async function fetchBilling() {
      setBillingLoading(true);
      setBillingError(false);
      try {
        if (!supabase) { setBillingError(true); return; }
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) { setBillingError(true); return; }

        const res = await fetch(`${API_URL}/api/square/billing`, {
          headers: { "Authorization": `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.ok) {
          setBilling(data.billing ?? []);
        } else {
          setBillingError(true);
        }
      } catch {
        setBillingError(true);
      } finally {
        setBillingLoading(false);
      }
    }
    fetchBilling();
  }, []);

  async function cancelSubscription() {
    setCancelError(null);
    setCancelBusy(true);
    try {
      if (!supabase) { setCancelError("Auth unavailable."); return; }
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) { setCancelError("Please sign in again."); return; }

      const res = await fetch(`${API_URL}/api/square/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.ok) {
        setCancelDone(true);
        setConfirmCancel(false);
      } else {
        setCancelError(data.error || "Couldn't cancel. Please try again.");
      }
    } catch {
      setCancelError("Network error. Try again.");
    } finally {
      setCancelBusy(false);
    }
  }

  const isPro = status === "active" || status === "trialing";
  const hasActiveSub = !!square_subscription_id && (status === "active" || status === "trialing" || status === "past_due");

  function planBadge() {
    if (cancelDone)             return { label: "Subscription cancelled — access continues until period ends", color: "#64748b", bg: "#1e293b", icon: "✅" };
    if (status === "trialing")  return { label: `Free trial — ends ${formatDate(trial_ends_at)}`, color: "#f59e0b", bg: "rgba(245,158,11,0.1)", icon: "⏳" };
    if (status === "active")    return { label: `Pro — renews ${formatDate(current_period_end)}`, color: "#0d9488", bg: "rgba(13,148,136,0.1)", icon: "✅" };
    if (status === "past_due")  return { label: "Payment issue — please contact support", color: "#ef4444", bg: "rgba(239,68,68,0.1)", icon: "⚠️" };
    return { label: "Free plan", color: "#64748b", bg: "#1e293b", icon: "🔓" };
  }

  const badge = planBadge();

  return (
    <div style={{ fontFamily: "Nunito, system-ui, sans-serif", background: "#0f172a", minHeight: "100vh", paddingBottom: 40 }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#134e4a,#0f172a)", padding: "28px 20px 20px", display: "flex", alignItems: "center", gap: 14 }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 20, padding: "8px 14px", color: "#94a3b8", fontWeight: 700, fontSize: 13, cursor: "pointer", minHeight: 44, flexShrink: 0 }}>
          ← Back
        </button>
        <div>
          <div style={{ fontSize: 20, fontWeight: 900, color: "#f1f5f9", fontFamily: "Fredoka One" }}>Billing & Payments</div>
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>MoneyMission Pro</div>
        </div>
      </div>

      <div style={{ padding: "20px 20px 0" }}>
        {/* Email */}
        <div style={{ background: "#1e293b", borderRadius: 16, padding: "16px 18px", marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 900, color: "#475569", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 4 }}>Account</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#f1f5f9", wordBreak: "break-all" }}>{email || "—"}</div>
        </div>

        {/* Plan status */}
        <div style={{ background: badge.bg, border: `1.5px solid ${badge.color}33`, borderRadius: 16, padding: "16px 18px", marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 900, color: "#475569", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 }}>Current plan</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20 }}>{badge.icon}</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: badge.color, lineHeight: 1.4 }}>{badge.label}</span>
          </div>
          {!isPro && !cancelDone && status !== "past_due" && (
            <button
              onClick={onUpgrade}
              style={{ marginTop: 12, width: "100%", padding: "12px", fontSize: 14, fontWeight: 800, background: "linear-gradient(135deg,#0d9488,#f59e0b)", color: "#fff", border: "none", borderRadius: 12, cursor: "pointer", minHeight: 44 }}
            >
              Upgrade to Pro →
            </button>
          )}
        </div>

        {/* Billing history */}
        <div style={{ background: "#1e293b", borderRadius: 16, padding: "16px 18px", marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 900, color: "#475569", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 12 }}>Payment history</div>

          {billingLoading ? (
            <div style={{ textAlign: "center", color: "#475569", fontSize: 13, fontWeight: 700, padding: "12px 0" }}>
              Loading…
            </div>
          ) : billingError ? (
            <div style={{ color: "#ef4444", fontSize: 13, fontWeight: 700, textAlign: "center", padding: "8px 0" }}>
              Couldn't load billing history. Try again later.
            </div>
          ) : billing.length === 0 ? (
            <div style={{ color: "#475569", fontSize: 13, fontWeight: 700, textAlign: "center", padding: "8px 0" }}>
              No charges yet — your trial is free.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {billing.map((row, i) => {
                const s = STATUS_STYLE[row.status] ?? STATUS_STYLE.UNPAID;
                return (
                  <div
                    key={row.id}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "11px 0",
                      borderBottom: i < billing.length - 1 ? "1px solid #334155" : "none",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "#f1f5f9" }}>
                        {formatAmount(row.amount, row.currency)}
                      </div>
                      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, marginTop: 2 }}>
                        {formatDate(row.date)}
                      </div>
                    </div>
                    <span style={{
                      fontSize: 10, fontWeight: 900, color: s.color, background: s.bg,
                      borderRadius: 8, padding: "3px 10px", whiteSpace: "nowrap",
                    }}>
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Cancel subscription */}
        {hasActiveSub && !cancelDone && (
          <div style={{ marginBottom: 12 }}>
            {cancelError && (
              <div style={{ background: "#fee2e2", borderRadius: 10, padding: "10px 14px", color: "#991b1b", fontSize: 13, fontWeight: 700, marginBottom: 10, borderLeft: "4px solid #ef4444" }}>
                {cancelError}
              </div>
            )}
            {!confirmCancel ? (
              <button
                onClick={() => setConfirmCancel(true)}
                style={{ width: "100%", padding: "14px", fontSize: 15, fontWeight: 800, background: "#1e293b", border: "1.5px solid #334155", color: "#94a3b8", borderRadius: 14, cursor: "pointer", minHeight: 48 }}
              >
                Cancel subscription
              </button>
            ) : (
              <div style={{ background: "#1e293b", borderRadius: 14, padding: "16px", border: "1.5px solid #ef444433" }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#f1f5f9", marginBottom: 6 }}>Cancel your subscription?</div>
                <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 700, marginBottom: 14, lineHeight: 1.5 }}>
                  You'll keep Pro access until {formatDate(current_period_end ?? trial_ends_at)}. This can't be undone.
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={() => setConfirmCancel(false)}
                    style={{ flex: 1, padding: "12px", fontSize: 14, fontWeight: 800, background: "transparent", border: "1.5px solid #334155", color: "#94a3b8", borderRadius: 12, cursor: "pointer", minHeight: 44 }}
                  >
                    Keep plan
                  </button>
                  <button
                    onClick={cancelSubscription}
                    disabled={cancelBusy}
                    style={{ flex: 1, padding: "12px", fontSize: 14, fontWeight: 800, background: "#ef4444", color: "#fff", border: "none", borderRadius: 12, cursor: cancelBusy ? "not-allowed" : "pointer", minHeight: 44 }}
                  >
                    {cancelBusy ? "Cancelling…" : "Yes, cancel"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sign out */}
        <div style={{ marginTop: 8 }}>
          <button
            onClick={onSignOut}
            style={{ width: "100%", padding: "14px", fontSize: 15, fontWeight: 800, background: "transparent", border: "1.5px solid #334155", color: "#ef4444", borderRadius: 14, cursor: "pointer", minHeight: 48 }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
